import React from 'react';
import PropTypes from 'prop-types';
import { getBaseButtonClasses, getFocusRingClasses, wrapWithLoadingSpinner, getIconSpacingClasses } from './buttonUtils';
import { surfaces, borders, textColors, interactive } from '../../tokens';

/**
 * SecondaryButton Component
 * 
 * A secondary action button with a white background and gray border.
 * Used for less prominent actions like "Cancel", "Back", or alternative options.
 * 
 * @example
 * // Basic secondary button
 * <SecondaryButton onClick={handleCancel}>
 *   Cancel
 * </SecondaryButton>
 * 
 * @example
 * // With icon
 * <SecondaryButton leftIcon={<ArrowLeftIcon />} onClick={goBack}>
 *   Back
 * </SecondaryButton>
 * 
 * @example
 * // Loading state
 * <SecondaryButton loading={isSaving}>
 *   Save Draft
 * </SecondaryButton>
 */
const SecondaryButton = ({
  children,
  onClick,
  type = 'button',
  variant = 'default',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  leftIcon = null,
  rightIcon = null,
  className = '',
  ...rest
}) => {
  const baseClasses = getBaseButtonClasses({ size, disabled, fullWidth, loading });
  const focusRingClasses = getFocusRingClasses('secondary');

  const variantClassesByVariant = {
    default: [
      surfaces.primary,
      surfaces.hover,
      textColors.primary,
      'border',
      borders.default,
      'shadow-sm',
    ],
    /**
     * Always-light secondary button (white background in BOTH light & dark themes).
     * Useful on marketing/hero areas where we want the button to remain white.
     */
    alwaysLight: [
      'bg-white',
      'hover:bg-gray-50',
      'dark:bg-white',
      'dark:hover:bg-gray-50',
      textColors.onLight,
      'border',
      'border-gray-200',
      'dark:border-gray-200',
      'shadow-sm',
    ],
    glass: [
      interactive.glassBtn,
      textColors.primary,
      'border',
      borders.glass,
      'shadow-sm',
    ],
  };

  const variantClasses = variantClassesByVariant[variant] || variantClassesByVariant.default;

  const classes = [
    baseClasses,
    ...variantClasses,
    focusRingClasses,
    className,
  ].join(' ');

  const buttonContent = (
    <>
      {!loading && leftIcon && (
        <span className={getIconSpacingClasses('left', !!children)}>
          {leftIcon}
        </span>
      )}
      {wrapWithLoadingSpinner(loading, children, size)}
      {!loading && rightIcon && (
        <span className={getIconSpacingClasses('right', !!children)}>
          {rightIcon}
        </span>
      )}
    </>
  );

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={classes}
      aria-busy={loading}
      {...rest}
    >
      {buttonContent}
    </button>
  );
};

SecondaryButton.propTypes = {
  /** Button content */
  children: PropTypes.node,
  /** Click handler */
  onClick: PropTypes.func,
  /** Button type */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Visual variant */
  variant: PropTypes.oneOf(['default', 'alwaysLight', 'glass']),
  /** Button size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Loading state */
  loading: PropTypes.bool,
  /** Full width button */
  fullWidth: PropTypes.bool,
  /** Icon to display on the left */
  leftIcon: PropTypes.node,
  /** Icon to display on the right */
  rightIcon: PropTypes.node,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default SecondaryButton;
